// BookingProgressBar — Sticky 5-step booking progress header.
// Reused across FlightDetailPage → PassengersPage → SeatMapPage → PaymentPage → ConfirmationPage.

import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/cn';

export type BookingStep =
  | 'flight-detail'
  | 'passengers'
  | 'seats'
  | 'payment'
  | 'confirmation';

interface Props {
  step: BookingStep;
}

const STEPS: Array<{ k: BookingStep; label: string }> = [
  { k: 'flight-detail', label: 'Fare' },
  { k: 'passengers',    label: 'Passengers' },
  { k: 'seats',         label: 'Seats' },
  { k: 'payment',       label: 'Payment' },
  { k: 'confirmation',  label: 'Confirmed' },
];

const BACK_ROUTE: Partial<Record<BookingStep, string>> = {
  'flight-detail': '/search/BLR-DPS',
  'passengers':    '/flights/sq-433',
  'seats':         '/passengers',
  'payment':       '/seats',
};

export function BookingProgressBar({ step }: Props) {
  const navigate = useNavigate();
  const activeIdx = STEPS.findIndex(s => s.k === step);
  const backRoute  = BACK_ROUTE[step];

  return (
    <div
      className="sticky top-0 z-30 w-full border-b border-white/[0.07] bg-[#0F1628]/95 backdrop-blur-md"
      style={{ padding: '10px 48px' }}
    >
      <div className="flex items-center gap-4 max-w-screen-xl mx-auto">
        {/* Back button */}
        {step !== 'confirmation' && backRoute && (
          <button
            onClick={() => navigate(backRoute)}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold flex-shrink-0',
              'text-[#8C98B0] border border-white/[0.07] bg-transparent',
              'hover:text-[#F4F3F0] hover:border-white/[0.14] transition-colors duration-200',
            )}
          >
            ← Back
          </button>
        )}

        {/* Route pill */}
        <div
          className="flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-bold text-[#F4F3F0] border border-white/[0.07] bg-[#0A0F1C] tracking-wide"
          style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
        >
          BLR → DPS · Mar 17
        </div>

        <div className="flex-1" />

        {/* Steps */}
        <div className="flex items-center">
          {STEPS.map((s, i) => {
            const done   = i < activeIdx;
            const active = i === activeIdx;

            return (
              <React.Fragment key={s.k}>
                <div className="flex items-center gap-1.5 px-2">
                  {/* Step circle */}
                  <div
                    className={cn(
                      'w-[22px] h-[22px] rounded-full flex items-center justify-center flex-shrink-0',
                      'text-[10px] font-bold transition-colors duration-200',
                      done || active
                        ? 'bg-[#FF5A3C] text-white'
                        : 'bg-white/[0.06] text-[#4E5E78]',
                    )}
                  >
                    {done ? '✓' : i + 1}
                  </div>
                  {/* Step label */}
                  <span
                    className={cn(
                      'text-[13px] font-semibold transition-colors duration-200',
                      active  ? 'text-[#F4F3F0]' :
                      done    ? 'text-[#8C98B0]' :
                                'text-[#4E5E78]',
                    )}
                    style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
                  >
                    {s.label}
                  </span>
                </div>

                {/* Connector */}
                {i < STEPS.length - 1 && (
                  <div
                    className="w-7 h-px flex-shrink-0 transition-colors duration-300"
                    style={{ background: i < activeIdx ? '#FF5A3C' : 'rgba(255,255,255,0.07)' }}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}
